import {AccessoryGroupData} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {STACK_UNIT_ID_PREFIX} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layer-model"
import {AccessoryStack, type AccessoryStackEntry} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessory-stack"
import {remapLayerParameters} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layers"
import {getDescriptorsOfType, InterfaceDescriptor, NumberInfo, StringInfo, TemplateReference, unwrapInterfaceId} from "@cm/template-nodes"
import {mattressIoMetaDataSchema, seamPathName} from "@cm/mattress-nodes"
import type {ParametersParameters} from "@cm/template-nodes"
import {getTemplateLegacyId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/asset-id"
import {getParameterStorage} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {getDeepestDescriptor} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/descriptors"

const ACCESSORY_COUNT_ID = "accessory-count" //number input, determines #accessories on a stack. Must be tagged with seamPathName/rank, determines an entry in cover components/seams. Occurs e.g. as c8c0686b-7266-44c0-9b81-10186c50b0e7/mattress/accessories-bottom-curve/accessory-count
const LAYER_SEGMENT_REGEX = new RegExp(`^${STACK_UNIT_ID_PREFIX}-(\\d+)$`)
const ACCESSORY_STACK_ID = "accessory-stack" //Same level as count-id, if count id != 0: ACCESSORY_STACK_ID: c8c0686b-7266-44c0-9b81-10186c50b0e7/mattress/accessories-bottom-curve/accessory-stack
const ACCESSORY_UNIT_ID_PREFIX = "accessory" //After ACCESSORY_STACK_ID, e.g. c8c0686b-7266-44c0-9b81-10186c50b0e7/mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory/accessory-type
const ACCESSORY_TYPE_ID = "accessory-type" //c8c0686b-7266-44c0-9b81-10186c50b0e7/mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory/accessory-type
const ACCESSORY_SEGMENT_REGEX = new RegExp(`^${ACCESSORY_UNIT_ID_PREFIX}-(\\d+)$`)
const ACCESSORY_TEMPLATE_ID = "accessory" //Defines the template of a specific accessory, e.g. c8c0686b-7266-44c0-9b81-10186c50b0e7/mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory
const PULLER_POSITION_ID = "puller-offset" //A puller must have exactly one of those inside its path
const ZIPPER_COLOR_ID = "zipper-color"
const PIPING_MATERIAL_ID = "material"
const PULLER_INSTANCE_ID = "puller"
const PULLER_TEMPLATE_INPUT_ID = "puller-template"

//Entry point to a seam, defines an entry in the ui. This is always the accessory-count input.
type SeamDescriptor = {
    descriptorId: string
    rank: number
}

/* ID helpers — construct and deconstruct descriptor paths */

function getAccessoryStackPrefix(seamId: string): string {
    return seamId + "/" + ACCESSORY_STACK_ID
}

// Extracts the accessory base id up to and including the "accessory-N" segment.
// e.g. "c8c0686b-.../mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory/accessory-type"
//    → "c8c0686b-.../mattress/accessories-bottom-curve/accessory-stack/accessory-0"
export function getAccessoryBaseId(descriptorId: string): string {
    const match = descriptorId.match(new RegExp(`^(.*/${ACCESSORY_UNIT_ID_PREFIX}-\\d+)`))
    if (!match) throw new Error(`No accessory segment found in "${descriptorId}"`)
    return match[1]
}

// Parameter filter prefix for separating accessory params from non-accessory params
// e.g. "mattress/accessories-bottom-curve/accessory-stack"
function getParamFilterPrefix(seamSuffix: string): string {
    return seamSuffix + "/" + ACCESSORY_STACK_ID
}

// Builds the template input key for a specific accessory index, relative to the template instance
// e.g. "mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory"
function getAccessoryTemplateInputId(seamSuffix: string, accessoryIndex: number): string {
    return seamSuffix + "/" + ACCESSORY_STACK_ID + "/" + ACCESSORY_UNIT_ID_PREFIX + "-" + accessoryIndex + "/" + ACCESSORY_TEMPLATE_ID
}

export function getAccessoryTemplateInputForBase(accessoryBase: string): string {
    return accessoryBase + "/" + ACCESSORY_TEMPLATE_ID
}

// Builds the count input key relative to the template instance
// e.g. "mattress/accessories-bottom-curve/accessory-count"
function getCountInputKey(seamSuffix: string): string {
    return seamSuffix + "/" + ACCESSORY_COUNT_ID
}

function extractAccessoryIndex(descriptorId: string): number | undefined {
    const segments = descriptorId.split("/")
    for (const segment of segments) {
        const match = segment.match(ACCESSORY_SEGMENT_REGEX)
        if (match) return parseInt(match[1], 10)
    }
    return undefined
}

function extractLayerInfo(descriptorId: string): {stackPrefix: string; layerIndex: number} | undefined {
    const segments = descriptorId.split("/")
    for (let i = 0; i < segments.length; i++) {
        const match = segments[i].match(LAYER_SEGMENT_REGEX)
        if (match) {
            const stackPrefix = segments.slice(0, i).join("/")
            return {stackPrefix, layerIndex: parseInt(match[1], 10)}
        }
    }

    return undefined
}

export function getZipperColorInputId(baseId: string) {
    return baseId + "/accessory/" + ZIPPER_COLOR_ID
}

export function getPipingMaterialInputId(baseId: string) {
    return baseId + "/accessory/" + PIPING_MATERIAL_ID
}

export function getPullerPositionInputId(baseId: string) {
    return baseId + "/" + PULLER_POSITION_ID
}

export function getPullerBaseIdFromZipperBaseId(zipperBaseId: string, pullerIndex: 0 | 1) {
    return `${zipperBaseId}/accessory/puller-${pullerIndex}`
}

// Inverse of getPullerBaseIdFromZipperBaseId: strips the trailing "/accessory/puller-{0|1}" to recover the zipper base id.
export function getZipperBaseIdFromPullerBase(pullerBase: string): string {
    const match = pullerBase.match(new RegExp(`^(.*)/accessory/${PULLER_INSTANCE_ID}-[01]$`))
    if (!match) throw new Error(`Not a puller base id: "${pullerBase}"`)
    return match[1]
}

export function getPullerBaseIdFromPullerAccessoryType(pullerAccessoryTypeId: string): string {
    //input sample:
    //"81262365-978b-4403-bf5d-2f94b41e1c49/mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory/puller-0/puller/accessory-type"

    //output for input:
    //"81262365-978b-4403-bf5d-2f94b41e1c49/mattress/accessories-bottom-curve/accessory-stack/accessory-0/accessory/puller-0"
    const re = new RegExp(`^(.*/${PULLER_INSTANCE_ID}-[01])/${ACCESSORY_TYPE_ID}$`)
    const match = pullerAccessoryTypeId.match(re)
    if (!match) {
        throw new Error(`Not a puller accessory-type id: "${pullerAccessoryTypeId}"`)
    }
    return match[1]
}

export function getPullerTemplateInputIdFromZipperBase(zipperBaseId: string, pullerIndex: 0 | 1) {
    return getPullerBaseIdFromZipperBaseId(zipperBaseId, pullerIndex) + "/puller-template"
}

export function getPullerTemplateInputIdFromPullerBase(pullerBase: string): string {
    return pullerBase + "/" + PULLER_TEMPLATE_INPUT_ID
}

/* Descriptor filters */

export function getAccessoryTypeDescriptor(descriptors: InterfaceDescriptor<unknown>[]): StringInfo | undefined {
    const allTypeDescriptors = getDescriptorsOfType(descriptors, StringInfo).filter((d) => d.props.id.endsWith(ACCESSORY_TYPE_ID))
    if (allTypeDescriptors.length === 0) return undefined
    return getDeepestDescriptor(allTypeDescriptors)
}

export function getPullerPositionDescriptors(descriptors: InterfaceDescriptor<unknown>[]): NumberInfo[] {
    return descriptors.filter((d): d is NumberInfo => d instanceof NumberInfo && d.props.id.endsWith(PULLER_POSITION_ID))
}

/* AccessoryModel */

function splitParams(params: ParametersParameters, filterPrefix: string): {accessoryParams: ParametersParameters; otherParams: ParametersParameters} {
    const accessoryParams: ParametersParameters = {}
    const otherParams: ParametersParameters = {}
    for (const [key, value] of Object.entries(params)) {
        if (key.includes(filterPrefix)) {
            accessoryParams[key] = value
        } else {
            otherParams[key] = value
        }
    }
    return {accessoryParams, otherParams}
}

export class AccessoryModel {
    private readonly context: ExtensionContext
    private readonly seamGroups: AccessoryGroupData[]
    private readonly stacks: Map<string, AccessoryStack>

    constructor(context: ExtensionContext) {
        this.context = context
        const seamDescriptors = collectSeamDescriptors(context)
        const rankGroups = groupByRank(seamDescriptors)
        const sortedEntries = sortAndValidateGroups(rankGroups)
        const groups = assignSeamNames(sortedEntries)
        this.stacks = buildStacks(groups, context)
        this.seamGroups = populateAccessories(groups, this.stacks)
    }

    getAccessoryGroups(): AccessoryGroupData[] {
        return this.seamGroups
    }

    async addAccessory(seamId: string, templateId: string): Promise<void> {
        const legacyId = await getTemplateLegacyId(templateId, this.context)
        const stack = this.getStack(seamId)
        const {mapping, newCount} = stack.buildAddMapping()

        this.context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key: seamSuffix} = getParameterStorage(templateGraph, seamId)
            const parametersNode = storingInstance.parameters.parameters
            const currentParams = parametersNode.parameters

            const filterPrefix = getParamFilterPrefix(seamSuffix)
            const {accessoryParams, otherParams} = splitParams(currentParams, filterPrefix)
            const remappedParams = remapLayerParameters(accessoryParams, ACCESSORY_UNIT_ID_PREFIX, mapping)

            const countInputKey = getCountInputKey(seamSuffix)
            // Optimistically set template on new accessory slot — descriptor doesn't exist yet, will be created on next sync
            const templateKey = getAccessoryTemplateInputId(seamSuffix, newCount - 1)
            const templateRef = new TemplateReference({parameters: {templateId: legacyId, templateRevisionId: null}})

            parametersNode.replaceParameters({
                ...otherParams,
                ...remappedParams,
                [countInputKey]: newCount,
                [templateKey]: templateRef,
            })
        })
    }

    removeAccessory(seamId: string, displayIndex: number): void {
        const stack = this.getStack(seamId)
        const {mapping, newCount} = stack.buildRemoveMapping(displayIndex)

        this.context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key: seamSuffix} = getParameterStorage(templateGraph, seamId)
            const parametersNode = storingInstance.parameters.parameters
            const currentParams = parametersNode.parameters

            const filterPrefix = getParamFilterPrefix(seamSuffix)
            const {accessoryParams, otherParams} = splitParams(currentParams, filterPrefix)
            const remappedParams = remapLayerParameters(accessoryParams, ACCESSORY_UNIT_ID_PREFIX, mapping)

            const countInputKey = getCountInputKey(seamSuffix)

            parametersNode.replaceParameters({
                ...otherParams,
                ...remappedParams,
                [countInputKey]: newCount,
            })
        })
    }

    moveAccessory(seamId: string, sourceDisplayIndex: number, targetDisplayIndex: number): void {
        const stack = this.getStack(seamId)
        const {mapping} = stack.buildMoveMapping(sourceDisplayIndex, targetDisplayIndex)

        this.context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key: seamSuffix} = getParameterStorage(templateGraph, seamId)
            const parametersNode = storingInstance.parameters.parameters
            const currentParams = parametersNode.parameters

            const filterPrefix = getParamFilterPrefix(seamSuffix)
            const {accessoryParams, otherParams} = splitParams(currentParams, filterPrefix)
            const remappedParams = remapLayerParameters(accessoryParams, ACCESSORY_UNIT_ID_PREFIX, mapping)

            parametersNode.replaceParameters({
                ...otherParams,
                ...remappedParams,
            })
        })
    }

    private getStack(seamId: string): AccessoryStack {
        const stack = this.stacks.get(seamId)
        if (!stack) throw new Error(`No accessory stack found for seamId: ${seamId}`)
        return stack
    }
}

/* Construction pipeline (used by AccessoryModel constructor) */

/*The accessory-count number input acts as seam identifier*/
function collectSeamDescriptors(context: ExtensionContext): SeamDescriptor[] {
    const descriptors = context.sceneManagerService.$descriptorList()
    const result: SeamDescriptor[] = []

    for (const d of descriptors) {
        const parsed = mattressIoMetaDataSchema.safeParse(d.props.metaData)
        if (!parsed.success) continue

        const seamPath = parsed.data.uiPaths.find((p) => p.name === seamPathName)
        if (!seamPath) continue

        if (!d.props.id.endsWith(ACCESSORY_COUNT_ID)) {
            throw new Error(`Seam descriptor "${d.props.id}" does not end with "${ACCESSORY_COUNT_ID}"`)
        }

        result.push({descriptorId: d.props.id, rank: seamPath.rank})
    }

    return result
}

/*All accessory stacks inside a fabric stack have the same rank at the moment - group them together in SeamDescriptor[]*/
function groupByRank(descriptors: SeamDescriptor[]): Map<number, SeamDescriptor[]> {
    const groups = new Map<number, SeamDescriptor[]>()
    for (const d of descriptors) {
        const group = groups.get(d.rank)
        if (group) {
            group.push(d)
        } else {
            groups.set(d.rank, [d])
        }
    }
    return groups
}

/*All accessory stacks inside a fabric stack have the same rank at the moment. They come in as ranked groups, sort within each group, then flatten into one array.*/
function sortAndValidateGroups(rankGroups: Map<number, SeamDescriptor[]>): SeamDescriptor[] {
    const sortedRanks = Array.from(rankGroups.keys()).sort((a, b) => a - b)
    const result: SeamDescriptor[] = []

    for (const rank of sortedRanks) {
        const group = rankGroups.get(rank)!

        if (group.length === 1) {
            result.push(group[0])
            continue
        }

        // Multiple entries at same rank — must be a fabric stack
        const layerEntries = group.map((d) => {
            const info = extractLayerInfo(d.descriptorId)
            if (!info) throw new Error(`Multiple seam descriptors at rank ${rank} but "${d.descriptorId}" does not contain a layer segment`)
            return {descriptor: d, ...info}
        })

        // Validate all share the same stack prefix
        const prefixes = new Set(layerEntries.map((e) => e.stackPrefix))
        if (prefixes.size > 1) throw new Error(`Seam descriptors at rank ${rank} belong to different stacks: ${Array.from(prefixes).join(", ")}`)

        // Sort descending by layer index
        layerEntries.sort((a, b) => b.layerIndex - a.layerIndex)
        result.push(...layerEntries.map((e) => e.descriptor))
    }

    return result
}

/* Use enumeration as seam name for now */
function assignSeamNames(entries: SeamDescriptor[]): AccessoryGroupData[] {
    const total = entries.length
    return entries.map((entry, i) => {
        const [seamPrefix] = unwrapInterfaceId(entry.descriptorId)
        return {
            seamName: `Seam ${total - i}`,
            seamId: seamPrefix ?? entry.descriptorId,
            accessories: [],
        }
    })
}

/* For each accessory group, build a stack instance/representation of its accessories. */
function buildStacks(groups: AccessoryGroupData[], context: ExtensionContext): Map<string, AccessoryStack> {
    const descriptors = context.sceneManagerService.$descriptorList()
    const result = new Map<string, AccessoryStack>()

    for (const group of groups) {
        const stackPrefix = getAccessoryStackPrefix(group.seamId) + "/"
        const matchingDescriptors = descriptors.filter((d) => d.props.id.startsWith(stackPrefix))

        const accessoryMap = new Map<number, {inputs: string[]; type: string}>()

        for (const d of matchingDescriptors) {
            const accessoryIndex = extractAccessoryIndex(d.props.id)
            if (accessoryIndex === undefined) continue

            let entry = accessoryMap.get(accessoryIndex)
            if (!entry) {
                entry = {inputs: [], type: ""}
                accessoryMap.set(accessoryIndex, entry)
            }

            if (d.props.type === "input") {
                entry.inputs.push(d.props.id) //those must be reodererd when something is inserted
            } else if (d.props.id.endsWith(ACCESSORY_TEMPLATE_ID + "/" + ACCESSORY_TYPE_ID)) {
                //accessory/accessory-type matches only what is on the actual stack and ignores nested pullers
                entry.type = String(d.props.value ?? "")
            }
        }

        const stackId = getAccessoryStackPrefix(group.seamId)
        const entries: AccessoryStackEntry[] = Array.from(accessoryMap.entries()).map(([index, data]) => ({
            accessoryId: index,
            stackId,
            inputParameterIds: data.inputs,
            type: data.type,
        }))

        result.set(group.seamId, new AccessoryStack(entries, true))
    }

    return result
}

function populateAccessories(groups: AccessoryGroupData[], stacks: Map<string, AccessoryStack>): AccessoryGroupData[] {
    for (const group of groups) {
        const stack = stacks.get(group.seamId)
        if (!stack) continue

        group.accessories = stack.getDisplayEntries().map((e) => ({
            type: e.type,
            inputParameterIds: e.inputParameterIds,
        }))
    }

    return groups
}

/* Exported queries */

/**
 * Collects accessory type and colormass ID pairs from the current descriptor list.
 * Used by getUsedElements to include accessories in the asset list.
 */
export async function collectAccessoryTemplates(_context: ExtensionContext, _errors?: string[]): Promise<{type: string; colormassId: string | undefined}[]> {
    // const descriptors = context.sceneManagerService.$descriptorList()
    // const typeDescriptors = getAccessoryTypeDescriptors(descriptors)
    // const results: {type: string; colormassId: string | undefined}[] = []

    // for (const typeDescriptor of typeDescriptors) {
    //     const elementType = typeDescriptor.props.value
    //     if (!elementType || elementType === "QuiltedFabric") continue

    //     try {
    //         const baseId = getAccessoryBaseId(typeDescriptor.props.id)
    //         const templateDescriptorId = getAccessoryTemplateInputForBase(baseId)
    //         const {value: templateRef} = getTemplateOrigin(templateDescriptorId, context)
    //         const colormassId = await getTemplateId(templateRef, context)
    //         results.push({type: elementType, colormassId})
    //         throw new Error("TEST")
    //     } catch (e) {
    //         errors?.push(`Failed to resolve accessory template (${elementType}): ${e instanceof Error ? e.message : String(e)}`)
    //     }
    // }

    // return results
    return [] //TODO: accessory collection
}
